import { useRouter } from 'next/router';
import React, { useEffect } from 'react';
import { IdentityValidationStageName } from '../../domain';
import { useIdentityValidationActions } from '..';
import { withAuth } from '@shared/Authentication';
import { Response } from '@shared/models';

const withIdentityValidation = (WrappedComponent: React.ElementType, urlReferrer?: string) => {
  const defaultIdentityValidationUrl = '/validacion-de-identidad';

  return withAuth((props: any) => {
    const router = useRouter();
    const referrer = router.asPath;

    const {
      result: identityValidation,
      loading,
      get: getIdentityValidation,
    } = useIdentityValidationActions().identityValidationQueryActions.useGetIdentityValidation();

    useEffect(() => {
      getIdentityValidation();
    }, []);

    useEffect(() => {
      const buildIdentityValidationRoute = () => ({
        pathname: !!referrer ? referrer.split('?')[0] : defaultIdentityValidationUrl,
        strQueryParams: Object.entries(router.query).reduce(
          (prev, current) => `${prev && prev + '&'}${current[0]}=${current[1]}`,
          ''
        ),
      });

      const isIdentityValidationRejected = () =>
        Response.isSucceed(identityValidation) &&
        identityValidation.result.stage === IdentityValidationStageName.REJECTED;

      const isIdentityValidationRetryableOrInitialized = () =>
        Response.isSucceed(identityValidation) &&
        (identityValidation.result.stage === IdentityValidationStageName.REJECTED_WITH_RETRY_OPTION ||
          identityValidation.result.stage === IdentityValidationStageName.INITIALIZED);

      const thereIsNotIdentityValidation = identityValidation === null;

      if (!loading && (thereIsNotIdentityValidation || isIdentityValidationRetryableOrInitialized())) {
        const { pathname, strQueryParams } = buildIdentityValidationRoute();
        const identityValidationURL = `${pathname}${strQueryParams && '?' + strQueryParams}`;

        router.push({
          pathname: defaultIdentityValidationUrl,
          query: {
            referrer: encodeURIComponent(identityValidationURL),
          },
        });
      } else if (!loading && isIdentityValidationRejected()) {
        router.push('/perfil');
      }
    }, [identityValidation, loading, referrer, router]);

    const isIdentityValidationApprovedOrPendingForReview = () =>
      Response.isSucceed(identityValidation) &&
      (identityValidation.result.stage === IdentityValidationStageName.APPROVED ||
        identityValidation.result.stage === IdentityValidationStageName.REVIEW_PENDING);

    const isValidStage = !loading && isIdentityValidationApprovedOrPendingForReview();

    return isValidStage ? <WrappedComponent {...props} /> : null;
  });
};

export { withIdentityValidation };
